import React, { createContext, useContext, useState, ReactNode } from 'react'
import { useCertificateContext } from './CertificateProvider'
import { usePinContext } from './PinProvider'
import { trpc } from '../utils'
import { useSnackbar } from 'notistack'

// Types
interface DocumentSignResult {
  success: boolean
  message: string
  name: string
}

interface SigningProgress {
  total: number
  completed: number
  failed: number
}

interface DocumentSigningContextType {
  udfFiles: File[] | null
  setUdfFiles: (files: File[] | null) => void
  documentSignResults: DocumentSignResult[]
  signingLoading: boolean
  signingProgress: SigningProgress
  signDocuments: () => Promise<void>
  signSingleDocument: (file: File) => Promise<any>
  clearResults: () => void
}

const DocumentSigningContext = createContext<
  DocumentSigningContextType | undefined
>(undefined)

export const DocumentSigningProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { selectedCertificate } = useCertificateContext()
  const { pinCode } = usePinContext()
  const { enqueueSnackbar } = useSnackbar()

  const [udfFiles, setUdfFiles] = useState<File[] | null>(null)
  const [documentSignResults, setDocumentSignResults] = useState<
    DocumentSignResult[]
  >([])
  const [signingLoading, setSigningLoading] = useState(false)
  const [signingProgress, setSigningProgress] = useState<SigningProgress>({
    total: 0,
    completed: 0,
    failed: 0,
  })

  const signSingleDocument = async (file: File) => {
    if (!selectedCertificate) {
      throw new Error('Lütfen bir sertifika seçin!')
    }

    if (!pinCode.trim()) {
      throw new Error('Lütfen PIN kodunu girin!')
    }

    const fileBuffer = await file.arrayBuffer()
    const fileBase64 = Buffer.from(fileBuffer).toString('base64')

    const result = await trpc.signDocument.mutate({
      udfFileBase64: fileBase64,
      signingTime: new Date().toISOString(),
      library: selectedCertificate.terminal.library,
      slotId: selectedCertificate.terminal.slotId,
      certSerialNumber: selectedCertificate.cert.serialNumber,
      pincode: pinCode,
      certificateBase64: selectedCertificate.cert.certificate,
      clientId: selectedCertificate.client.clientId,
    })

    setDocumentSignResults((prev) => [
      ...prev,
      {
        success: result.success,
        message: result.message,
        name: file.name,
      },
    ])

    if (result.success) {
      // Download signed document
      const signedUdfBlob = new Blob([
        Buffer.from(result.signedUdfFileBase64, 'base64'),
      ])
      const url = URL.createObjectURL(signedUdfBlob)
      const a = document.createElement('a')
      a.href = url
      a.download = file.name
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    }

    return result
  }

  const signDocuments = async () => {
    if (!udfFiles || udfFiles.length === 0) {
      enqueueSnackbar('Lütfen en az bir UDF dosyası seçin!', { variant: 'warning' })
      return
    }

    setSigningLoading(true)
    setSigningProgress({
      total: udfFiles.length,
      completed: 0,
      failed: 0,
    })
    setDocumentSignResults([])

    for (const file of udfFiles) {
      try {
        await signSingleDocument(file)
        setSigningProgress((prev) => ({
          ...prev,
          completed: prev.completed + 1,
        }))
      } catch (error) {
        console.error('Failed to sign document:', error)
        setSigningProgress((prev) => ({
          ...prev,
          failed: prev.failed + 1,
        }))
      }
    }

    setSigningLoading(false)
  }

  const clearResults = () => {
    setDocumentSignResults([])
    setSigningProgress({
      total: 0,
      completed: 0,
      failed: 0,
    })
  }

  const contextValue: DocumentSigningContextType = {
    udfFiles,
    setUdfFiles,
    documentSignResults,
    signingLoading,
    signingProgress,
    signDocuments,
    signSingleDocument,
    clearResults,
  }

  return (
    <DocumentSigningContext.Provider value={contextValue}>
      {children}
    </DocumentSigningContext.Provider>
  )
}

export const useDocumentSigningContext = () => {
  const context = useContext(DocumentSigningContext)
  if (context === undefined) {
    throw new Error(
      'useDocumentSigningContext must be used within a DocumentSigningProvider',
    )
  }
  return context
}
